import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useCollapse } from 'react-collapsed';
import Skeleton from 'react-loading-skeleton';
import dayjs from 'dayjs';
import time from 'utils/time';
import DescriptionTinyEditor from 'components/Dashboard/UserContent/ui/DescriptionTinyEditor';
import { CalendarPopup } from 'components/Dashboard/Filters/ui/DateFilter';
import DropdownComponent from 'components/Settings/WebsiteFeeds/DropdownComponent';
import { store } from 'components/Store';
import PeopleAsyncSelect from './PeopleAsyncSelect';
import MembersSelect from './MembersSelect';
import { AddTagsContent } from 'components/Dashboard/Influence/CrmContacts/AddTagsModal/AddTagsModal';
import useLoadInteractionTagOptions from 'components/Dashboard/Filters/useLoadInteractionTagOptions';
import getInteractionDisplayType from './getInteractionDisplayType';
import EmailRecipients from 'components/Dashboard/Influence/CrmEmails/CrmEmailComponent/EmailRecipients/EmailRecipients';

export const INTERACTION_TYPES = ['Email', 'Meeting', 'Phone call', 'Social media', 'Events', 'Other'];

// The form has no Title field, but the backend still requires a non-empty title - every
// create/update sends this as a placeholder so the record still has something to fall back to
// anywhere a title is displayed (list items, breadcrumbs).
export const DEFAULT_INTERACTION_TITLE = 'Untitled interaction';

export const InteractionFormFieldsSkeleton = () => (
  <>
    <div className='d-flex flex-column flex-sm-row mb-3'>
      <div style={{ minWidth: 0, flex: '1 1 0%' }}>
        <Skeleton height={19} width={60} className='mb-1' />
        <Skeleton height={38} />
      </div>
      <div className='mt-3 mt-sm-0 ml-sm-3' style={{ minWidth: 0, flex: '1 1 0%' }}>
        <Skeleton height={19} width={60} className='mb-1' />
        <Skeleton height={38} />
      </div>
    </div>
    <div className='d-flex flex-column flex-sm-row mb-3'>
      <div style={{ minWidth: 0, flex: '1 1 0%' }}>
        <Skeleton height={19} width={80} className='mb-1' />
        <Skeleton height={38} />
      </div>
      <div className='mt-3 mt-sm-0 ml-sm-3' style={{ minWidth: 0, flex: '1 1 0%' }}>
        <Skeleton height={19} width={80} className='mb-1' />
        <Skeleton height={38} />
      </div>
    </div>
    <div className='d-flex flex-column flex-sm-row mb-3'>
      <div style={{ minWidth: 0, flex: '1 1 0%' }}>
        <Skeleton height={19} width={140} className='mb-1' />
        <Skeleton height={176} />
      </div>
      <div className='mt-3 mt-sm-0 ml-sm-3' style={{ minWidth: 0, flex: '1 1 0%' }}>
        <Skeleton height={19} width={100} className='mb-1' />
        <Skeleton height={176} />
      </div>
    </div>
    <div className='mb-3'>
      <Skeleton height={19} width={100} className='mb-2' />
      <Skeleton height={200} />
    </div>
  </>
);

// Bumps whenever `openDropdown` changes to something other than `id` - used as a close signal so
// a field's own dropdown/menu closes when a DIFFERENT field opens, without also firing (and
// self-closing it) on the same change that opens it.
const useCloseTickExcept = (openDropdown, id) => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (openDropdown !== id) setTick((t) => t + 1);
  }, [openDropdown, id]);
  return tick;
};

const RemoveSelectionButton = ({ id, count, singular, plural, onRemoveAll, openDropdown, setOpenDropdown }) => {
  if (!count) return null;
  const isOpen = openDropdown === id;
  return (
    <div className='position-relative d-inline-block flex-shrink-0'>
      <button
        type='button'
        className='general-button circle-button hover-background-gray-05 p-1'
        onClick={() => setOpenDropdown((d) => (d === id ? null : id))}
      >
        <span className='icon-three-dots text-main-blue-40' />
      </button>
      {isOpen && (
        <>
          <div className='general-close-background z-max' onClick={() => setOpenDropdown(null)} />
          <div className='crm-actions-box box box-shadow specific-top-padding-6 specific-bottom-padding-6'>
            <button
              type='button'
              className='general-button specific-right-padding-35 specific-left-padding-16 d-flex align-items-center list-action-button'
              onClick={() => {
                onRemoveAll();
                setOpenDropdown(null);
              }}
            >
              <span className='icon-remove-stakeholders text-dark-red' />
              <span className='specific-left-padding-14 text-dark-red'>
                {count === 1 ? `Remove this ${singular}` : `Remove all ${count} ${plural}`}
              </span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const InteractionFormFields = ({
  form,
  onChange,
  recipientsSendRecords,
  isLoadingRecipients,
  senderEmailAddress,
  onEditorReady,
  openDropdown: openDropdownProp,
  setOpenDropdown: setOpenDropdownProp,
}) => {
  const { state } = useContext(store);
  const teamName = state.team?.teamName ?? 'Organisation';
  const organisationName = state.activePlan?.organisationName ?? teamName;
  const loadTagOptions = useLoadInteractionTagOptions();
  const [recipientsSearchValue, setRecipientsSearchValue] = useState('');
  const [recipientsActiveOption, setRecipientsActiveOption] = useState('All');
  const [isRecipientsExpanded, setIsRecipientsExpanded] = useState(false);
  const { getCollapseProps, getToggleProps } = useCollapse({ isExpanded: isRecipientsExpanded });

  const [calendarState, setCalendarState] = useState({
    active: false,
    filterProperty: '',
    selectedDay: form.date ? time.parseCalendarDateString(form.date).toDate() : new Date(),
    name: '',
    range: { from: '', to: '' },
  });

  // Callers embedding this inside a modal (LogInteractionModal) need to know, at the moment the
  // overlay is clicked, whether a dropdown is open - so this has to be liftable. Standalone callers
  // (the interaction detail page) get a plain local fallback instead of having to wire it up.
  const [localOpenDropdown, setLocalOpenDropdown] = useState(null);
  const openDropdown = openDropdownProp ?? localOpenDropdown;
  const setOpenDropdown = setOpenDropdownProp ?? setLocalOpenDropdown;
  const onMenuCloseFor = (id) => () => setOpenDropdown((d) => (d === id ? null : d));

  const typeCloseTick = useCloseTickExcept(openDropdown, 'type');
  const initiatorCloseTick = useCloseTickExcept(openDropdown, 'initiator');
  const tagsCloseTick = useCloseTickExcept(openDropdown, 'tags');

  const [scrollCloseTick, setScrollCloseTick] = useState(0);

  const formTopRef = useRef(null);
  useEffect(() => {
    // DropdownComponent's fixedDropdown menus are position:fixed, calculated once on open, so they
    // go stale (detached from their button) once the modal's scroll container moves under them.
    const scrollParent = formTopRef.current?.closest('.custom-scrollbars__content');
    if (!scrollParent) return;
    const closeAll = () => {
      setOpenDropdown(null);
      setScrollCloseTick((t) => t + 1);
    };
    scrollParent.addEventListener('scroll', closeAll);
    return () => scrollParent.removeEventListener('scroll', closeAll);
  }, []);

  const openCalendar = () => {
    setOpenDropdown('date');
    setCalendarState((s) => ({
      ...s,
      active: true,
      selectedDay: form.date ? time.parseCalendarDateString(form.date).toDate() : new Date(),
    }));
  };

  useEffect(() => {
    if (openDropdown !== 'date') setCalendarState((s) => (s.active ? { ...s, active: false } : s));
  }, [openDropdown]);

  // The reverse of the effect above: CalendarPopup is its own nested modal, so it can close itself
  // (its own overlay click, its close animation) without going through setOpenDropdown - without
  // this, openDropdown would stay stuck on 'date' after that.
  useEffect(() => {
    if (!calendarState.active) setOpenDropdown((d) => (d === 'date' ? null : d));
  }, [calendarState.active]);

  // Interactions auto-created from a sent CRM email are locked to "Email campaign" - can't be
  // retyped since they're tied to that email's send record, not freely editable like other types.
  const isEmailCampaign = !!form.sourceCrmEmailId;
  const typeDisplayValue = getInteractionDisplayType({
    interactionType: form.type,
    sourceCrmEmailId: form.sourceCrmEmailId,
  });

  const initiatorLabel = isEmailCampaign
    ? senderEmailAddress
    : form.initiator === 'Organisation'
      ? teamName
      : form.initiator === 'Stakeholder'
        ? 'Stakeholder'
        : null;

  // DropdownComponent closes itself whenever its `mainItem` prop changes identity, so this must stay
  // referentially stable across unrelated re-renders (e.g. opening this dropdown) or it self-closes
  // on the same render that opens it, requiring a second click to actually keep it open.
  const typeMainItem = useCallback(
    () => (
      <span className={`hidden-lines hidden-one-line ${typeDisplayValue ? '' : 'text-main-blue-40'}`}>
        {typeDisplayValue || 'Select type'}
      </span>
    ),
    [typeDisplayValue]
  );

  const initiatorPlaceholder = isEmailCampaign && isLoadingRecipients ? '' : 'Select initiator';
  const initiatorMainItem = useCallback(
    () => (
      <span className={`hidden-lines hidden-one-line ${initiatorLabel ? '' : 'text-main-blue-40'}`}>
        {initiatorLabel || initiatorPlaceholder}
      </span>
    ),
    [initiatorLabel, initiatorPlaceholder]
  );

  return (
    <>
      <div className='d-flex flex-column flex-sm-row mb-3' ref={formTopRef}>
        <div style={{ minWidth: 0, flex: '1 1 0%' }}>
          <label className='d-block mb-2 paragraph-p1 font-weight-bold'>Type</label>
          <DropdownComponent
            mainItem={typeMainItem}
            fixedDropdown
            waitOnOpen={false}
            mainItemClass='form-control border-general border-grey no-focus-outline interaction-form-select'
            dropdownClass='shadow-open-dropdown interaction-form-dropdown'
            fixedTopSpace={4}
            onOpen={() => setOpenDropdown('type')}
            onClose={onMenuCloseFor('type')}
            closeSignal={scrollCloseTick + typeCloseTick}
            disabled={isEmailCampaign}
            hideArrowDisabled={isEmailCampaign}
          >
            <ul className='policy-list p-0 m-0'>
              {INTERACTION_TYPES.map((type) => (
                <li
                  key={type}
                  onClick={() => onChange('type', type)}
                  className='list-action-button py-2 px-3 flex-centered pointer'
                >
                  <span className='flex-grow-1'>{getInteractionDisplayType({ interactionType: type })}</span>
                  {form.type === type && <span className='icon-tick paragraph-p1' />}
                </li>
              ))}
            </ul>
          </DropdownComponent>
        </div>
        <div className='mt-3 mt-sm-0 ml-sm-3' style={{ minWidth: 0, flex: '1 1 0%' }}>
          <label className='d-block mb-2 paragraph-p1 font-weight-bold'>Date</label>
          <button
            type='button'
            className={`form-control border-general border-grey text-left no-focus-outline d-flex align-items-center justify-content-between ${
              isEmailCampaign ? 'disabled-hour-select' : ''
            }`}
            onClick={openCalendar}
            disabled={isEmailCampaign}
          >
            <span className={form.date ? 'text-main-blue' : 'text-main-blue-40'}>
              {form.date ? dayjs(form.date).format('D MMMM YYYY') : 'Select date'}
            </span>
            {!isEmailCampaign && <span className='icon-calendar' />}
          </button>
          <CalendarPopup
            showPopup={calendarState.active}
            calendarState={calendarState}
            setCalendarState={setCalendarState}
            ApplyCalendarFilters={() => onChange('date', dayjs(calendarState.selectedDay).format('YYYY-MM-DD'))}
            setShowPopup={(val) => setCalendarState((s) => ({ ...s, active: val }))}
            noFutureDatesAllowed={false}
            selectedMonth={calendarState.selectedDay ? dayjs(calendarState.selectedDay).toDate() : new Date()}
          />
        </div>
      </div>

      <div className='d-flex flex-column flex-sm-row mb-3'>
        <div style={{ minWidth: 0, flex: '1 1 0%' }}>
          <label className='d-block mb-2 paragraph-p1 font-weight-bold'>
            {isEmailCampaign ? 'Sender' : 'Initiator'}
          </label>
          <DropdownComponent
            mainItem={initiatorMainItem}
            fixedDropdown
            waitOnOpen={false}
            mainItemClass='form-control border-general border-grey no-focus-outline interaction-form-select'
            dropdownClass='shadow-open-dropdown interaction-form-dropdown'
            fixedTopSpace={4}
            onOpen={() => setOpenDropdown('initiator')}
            onClose={onMenuCloseFor('initiator')}
            closeSignal={scrollCloseTick + initiatorCloseTick}
            disabled={isEmailCampaign}
            hideArrowDisabled={isEmailCampaign}
          >
            <ul className='policy-list p-0 m-0'>
              {[
                { value: 'Organisation', label: teamName },
                { value: 'Stakeholder', label: 'Stakeholder' },
              ].map(({ value, label }) => (
                <li
                  key={value}
                  onClick={() => onChange('initiator', value)}
                  className='list-action-button py-2 px-3 flex-centered pointer'
                >
                  <span className='flex-grow-1'>{label}</span>
                  {form.initiator === value && <span className='icon-tick paragraph-p1' />}
                </li>
              ))}
            </ul>
          </DropdownComponent>
        </div>
        <div className='mt-3 mt-sm-0 ml-sm-3' style={{ minWidth: 0, flex: '1 1 0%' }}>
          <label className='d-block mb-2 paragraph-p1 font-weight-bold'>Add a tag</label>
          <div
            className={`form-control border-general border-grey no-focus-outline interaction-form-select p-0 w-100 ${
              openDropdown === 'tags' ? 'hour-select-opendropdown' : ''
            }`}
            style={{ height: '38px' }}
          >
            <AddTagsContent
              customContact
              roundedTagChips
              singleLine
              tags={form.tags ?? []}
              setTags={(newValue) => onChange('tags', newValue ?? [])}
              loadTagOptions={loadTagOptions}
              onMenuOpen={() => setOpenDropdown('tags')}
              onMenuClose={onMenuCloseFor('tags')}
              closeSignal={tagsCloseTick}
            />
          </div>
        </div>
      </div>

      {isEmailCampaign ? (
        <div className='mb-3 interaction-recipients-wrapper'>
          <label className='d-block mb-2 paragraph-p1 font-weight-bold'>Recipients</label>
          <button
            type='button'
            className={`general-button d-block w-100 mb-0 hour-select px-3 form-control border-general border-grey no-focus-outline interaction-form-select text-left light-blue-background-004-hover ${
              isRecipientsExpanded ? 'hour-select-opendropdown' : ''
            }`}
            {...getToggleProps({ onClick: () => setIsRecipientsExpanded((expanded) => !expanded) })}
          >
            <span className='hidden-lines hidden-one-line'>
              {isLoadingRecipients
                ? 'Loading recipients…'
                : `See engagement data for ${recipientsSendRecords?.length ?? 0} recipient${
                    (recipientsSendRecords?.length ?? 0) === 1 ? '' : 's'
                  }`}
            </span>
          </button>
          <div {...getCollapseProps()}>
            <div className='pt-3'>
              <EmailRecipients
                sendRecords={recipientsSendRecords ?? []}
                emailId={true}
                searchValue={recipientsSearchValue}
                setSearchValue={setRecipientsSearchValue}
                activeOption={recipientsActiveOption}
                setActiveOption={setRecipientsActiveOption}
                isLoading={isLoadingRecipients}
                fixedHeight={228}
                mainContentScrollbar=''
                showActionHeader
              />
            </div>
          </div>
        </div>
      ) : (
        <div className='d-flex flex-column flex-sm-row mb-3'>
          <div className='d-flex flex-column' style={{ minWidth: 0, flex: '1 1 0%' }}>
            <label className='d-block mb-2 paragraph-p1 font-weight-bold text-truncate' style={{ minWidth: 0 }}>
              People from {organisationName}
            </label>
            <MembersSelect
              value={form.members ?? []}
              onChange={(selected) => onChange('members', selected)}
              placeholder='Add person'
              menuIsOpen={openDropdown === 'members'}
              onMenuOpen={() => setOpenDropdown('members')}
              onMenuClose={onMenuCloseFor('members')}
              cornerAction={
                <RemoveSelectionButton
                  id='members-menu'
                  count={form.members?.length ?? 0}
                  singular='person'
                  plural='people'
                  onRemoveAll={() => onChange('members', [])}
                  openDropdown={openDropdown}
                  setOpenDropdown={setOpenDropdown}
                />
              }
            />
          </div>
          <div className='mt-3 mt-sm-0 ml-sm-3 d-flex flex-column' style={{ minWidth: 0, flex: '1 1 0%' }}>
            <label className='d-block mb-2 paragraph-p1 font-weight-bold'>Stakeholders</label>
            <PeopleAsyncSelect
              value={form.stakeholders ?? []}
              onChange={(selected) => onChange('stakeholders', selected ?? [])}
              placeholder='Add stakeholder'
              menuIsOpen={openDropdown === 'stakeholders'}
              onMenuOpen={() => setOpenDropdown('stakeholders')}
              onMenuClose={onMenuCloseFor('stakeholders')}
              cornerAction={
                <RemoveSelectionButton
                  id='stakeholders-menu'
                  count={form.stakeholders?.length ?? 0}
                  singular='stakeholder'
                  plural='stakeholders'
                  onRemoveAll={() => onChange('stakeholders', [])}
                  openDropdown={openDropdown}
                  setOpenDropdown={setOpenDropdown}
                />
              }
            />
          </div>
        </div>
      )}

      <div className='mb-3'>
        <label className='d-block mb-2 paragraph-p2 font-weight-bold'>Description</label>
        <div className='influence-description interaction-form-description w-100' style={{ display: 'flow-root' }}>
          <DescriptionTinyEditor
            value={form.description}
            onChangeFunction={(content) => onChange('description', content)}
            minHeight={200}
            maxHeight={300}
            hideSkeleton
            autoresize_bottom_margin={14}
            disableAutofocus
            toolbarLocation='bottom'
            colorPickerPosition='top'
            newline_behavior='block'
            additionalStyles='p { margin: 0; }'
            disabled={isEmailCampaign}
            onInitFunction={onEditorReady}
          />
        </div>
      </div>
    </>
  );
};

export default InteractionFormFields;
